import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { authService } from '../../services/authService';
import { vmsService } from '../../services/vmsService';
import { AddEntryModal } from './components/AddEntryModal';
import { DocVersionsModal } from './components/DocVersionsModal';
import { RootState } from '../../store';

const getInvoiceHighlightStyle = (receivedDateStr: string | null, paymentStatus: string | null) => {
  if (paymentStatus === 'paid' || !receivedDateStr) return {};
  const receivedDate = new Date(receivedDateStr);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - receivedDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 30) {
    return { backgroundColor: '#e6fbf1', color: '#044e39', borderLeft: '4px solid #10b981' };
  } else if (diffDays <= 60) {
    return { backgroundColor: '#fff7ed', color: '#7c2d12', borderLeft: '4px solid #f97316' };
  } else {
    return { backgroundColor: '#fef2f2', color: '#7f1d1d', borderLeft: '4px solid #ef4444' };
  }
};

const VendorDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [vendor, setVendor] = useState<any>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<any | null>(null);
  const [editingEntry, setEditingEntry] = useState<any | null>(null);

  const [filterDocType, setFilterDocType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const [activeTab, setActiveTab] = useState<'details' | 'documents' | 'invoices' | 'products'>('details');
  const [products, setProducts] = useState<any[]>([]);
  const [banks, setBanks] = useState<any[]>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isFinanceDept = currentUser?.department?.toLowerCase() === 'finance';
  const canDownload = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  const fetchDetails = async () => {
    try {
      if (!id) return;
      const [vendorData, entriesData, productsData, banksData] = await Promise.all([
        vmsService.getVendor(id),
        vmsService.getVendorDocDetails(id),
        vmsService.getVendorProducts(id),
        vmsService.getVendorBanks(id),
      ]);
      setVendor(vendorData);
      setEntries(entriesData);
      setProducts(productsData);
      setBanks(banksData.filter((b: any) => b.status === 'active'));
    } catch (err) {
      console.error('Failed to fetch vendor details:', err);
    }
  };

  React.useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    fetchDetails();
  };

  const handleUploadVersion = async (entryId: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await vmsService.uploadVendorDocVersion(id!, entryId, formData);
      fetchDetails();
    } catch (err) {
      alert('Failed to upload new version.');
    }
  };

  const handleDownloadCurrent = async (entryId: number) => {
    try {
      const res = await vmsService.downloadCurrentVendorDoc(id!, entryId);
      if (res?.url) window.open(res.url, '_blank');
    } catch (err) {
      alert('Failed to generate download link.');
    }
  };

  const handleDeactivate = async (entryId: number) => {
    if (!window.confirm('Deactivate this document entry?')) return;
    try {
      await vmsService.deleteVendorDocDetail(id!, entryId);
      fetchDetails();
    } catch (err) {
      alert('Failed to deactivate entry.');
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    dispatch(logout());
    navigate('/');
  };

  if (!vendor) {
    return <div className="page-container">Loading vendor details...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Link to="/vms" style={{ color: 'var(--text-dark)', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <h1 className="page-title" style={{ margin: 0 }}>{vendor.vendor_name} ({vendor.vendor_code || vendor.id})</h1>
          </div>
          <div className="action-cell">
            <button onClick={() => navigate('/profile')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', display: 'flex', alignItems: 'center' }} title="Profile">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
        <p className="page-description" style={{ margin: 0, marginTop: '0.5rem', paddingLeft: '2.5rem' }}>View transactions, add entries, and manage documents for this vendor.</p>
      </div>

      <div className="tabs-nav" style={{ display: 'flex', gap: '2rem', borderBottom: '1px solid var(--border)', marginBottom: '1.5rem', paddingLeft: '1rem' }}>
        <button
          onClick={() => setActiveTab('details')}
          style={{
            background: 'none', border: 'none', padding: '1rem 0', cursor: 'pointer', fontSize: '1rem', fontWeight: 500,
            color: activeTab === 'details' ? 'var(--primary)' : 'var(--text-light)',
            borderBottom: activeTab === 'details' ? '2px solid var(--primary)' : '2px solid transparent',
            outline: 'none'
          }}
        >
          Vendor Details
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          style={{
            background: 'none', border: 'none', padding: '1rem 0', cursor: 'pointer', fontSize: '1rem', fontWeight: 500,
            color: activeTab === 'invoices' ? 'var(--primary)' : 'var(--text-light)',
            borderBottom: activeTab === 'invoices' ? '2px solid var(--primary)' : '2px solid transparent',
            outline: 'none'
          }}
        >
          Invoices
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          style={{
            background: 'none', border: 'none', padding: '1rem 0', cursor: 'pointer', fontSize: '1rem', fontWeight: 500,
            color: activeTab === 'documents' ? 'var(--primary)' : 'var(--text-light)',
            borderBottom: activeTab === 'documents' ? '2px solid var(--primary)' : '2px solid transparent',
            outline: 'none'
          }}
        >
          Documents
        </button>
        <button
          onClick={() => setActiveTab('products')}
          style={{
            background: 'none', border: 'none', padding: '1rem 0', cursor: 'pointer', fontSize: '1rem', fontWeight: 500,
            color: activeTab === 'products' ? 'var(--primary)' : 'var(--text-light)',
            borderBottom: activeTab === 'products' ? '2px solid var(--primary)' : '2px solid transparent',
            outline: 'none'
          }}
        >
          Commercials/Quotation
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="vendor-info-card" style={{ display: 'block', padding: '1.5rem 2rem' }}>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>Vendor Contact Info</h3>
          <div className="vendor-info-grid">
            <div>
              <p className="info-label">Contact Person</p>
              <p className="info-value">{vendor.contact_person || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Contact Email</p>
              <p className="info-value">{vendor.email}</p>
            </div>
            <div>
              <p className="info-label">Mobile Number</p>
              <p className="info-value">{vendor.mobile_number || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Department</p>
              <p className="info-value">{vendor.department || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Vendor Type</p>
              <p className="info-value">{vendor.vendor_type || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Vendor Category</p>
              <p className="info-value">{vendor.vendor_category === 'one_time' ? 'One-time' : vendor.vendor_category === 'regular' ? 'Regular' : vendor.vendor_category === 'retainer' ? 'Retainer' : 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Nature of Service</p>
              <p className="info-value">{vendor.nature_of_service || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Status</p>
              <span className={`status-badge ${vendor.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                {vendor.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <h3 style={{ margin: '1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>Empanelment & Addresses</h3>
          <div className="vendor-info-grid">
            <div>
              <p className="info-label">Date of Empanelment</p>
              <p className="info-value">{vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Empanelled By</p>
              <p className="info-value">{vendor.empanelled_by || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">State</p>
              <p className="info-value">{vendor.state || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Permanent Address</p>
              <p className="info-value">{vendor.permanent_address || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Office Address</p>
              <p className="info-value">{vendor.office_address || 'N/A'}</p>
            </div>
          </div>

          <h3 style={{ margin: '1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>Business & Tax Information</h3>
          <div className="vendor-info-grid">
            <div>
              <p className="info-label">Udyam Aadhaar</p>
              <p className="info-value">{vendor.udyam_number || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">PAN Number</p>
              <p className="info-value">{vendor.pan_number || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">GST Number</p>
              <p className="info-value">{vendor.gst_number || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">GST Applicable</p>
              <p className="info-value">{vendor.gst_applicable ? 'Yes' : 'No'}</p>
            </div>
            {vendor.gst_applicable && (
              <div>
                <p className="info-label">GST Types</p>
                <p className="info-value">{vendor.gst_types?.length ? vendor.gst_types.join(', ') : 'N/A'}</p>
              </div>
            )}
            <div>
              <p className="info-label">TDS Applicable</p>
              <p className="info-value">{vendor.tds_applicable ? 'Yes' : 'No'}</p>
            </div>
          </div>

          {(vendor.partner_type === 'lsp' || vendor.partner_type === 'lender') && isFinanceDept && (
            <>
              <h3 style={{ margin: '1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>Partner Commercial Details</h3>
              <div className="vendor-info-grid">
                <div>
                  <p className="info-label">Partner Type</p>
                  <p className="info-value">{vendor.partner_type === 'lsp' ? 'LSP' : 'Lender Partner'}</p>
                </div>
                <div>
                  <p className="info-label">Payout on Loan Amount</p>
                  <p className="info-value">{vendor.payout_on_loan_amount ? `${vendor.payout_on_loan_amount}%` : 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Base Rate</p>
                  <p className="info-value">{vendor.base_rate ? `${vendor.base_rate}% (${vendor.base_rate_type})` : 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Relationship Type</p>
                  <p className="info-value">{vendor.relationship_type || 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">FLDG / Cash Margin</p>
                  <p className="info-value">{vendor.fldg || 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Processing Fee</p>
                  <p className="info-value">{vendor.processing_fee_percent ? `${vendor.processing_fee_percent}%` : 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Processing Fee Share</p>
                  <p className="info-value">{vendor.processing_fee_share ? `${vendor.processing_fee_share}%` : 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Login Fees</p>
                  <p className="info-value">{vendor.login_fees || 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">HSN Code</p>
                  <p className="info-value">{vendor.hsn_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Invoicing Period</p>
                  <p className="info-value">{vendor.invoicing_period || 'N/A'}</p>
                </div>

                {vendor.partner_type === 'lender' && (
                  <>
                    <div>
                      <p className="info-label">Base Hurdle Rate</p>
                      <p className="info-value">{vendor.hurdle_rate_base ? `${vendor.hurdle_rate_base}%` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="info-label">Cap Hurdle Rate</p>
                      <p className="info-value">{vendor.hurdle_rate_cap ? `${vendor.hurdle_rate_cap}%` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="info-label">Penal Charges Applicable</p>
                      <p className="info-value">{vendor.penal_charges_applicable ? 'Yes' : 'No'}</p>
                    </div>
                    {vendor.penal_charges_applicable && (
                      <div>
                        <p className="info-label">Penal Charges Amount</p>
                        <p className="info-value">{vendor.penal_charges_amount || 'N/A'}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          <h3 style={{ margin: '1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>Bank Details</h3>
          {banks.length > 0 ? banks.map((bank, index) => (
            <div key={bank.id || index} style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', position: 'relative' }}>
              {bank.is_default && (
                <span style={{ position: 'absolute', top: '1rem', right: '1rem', backgroundColor: '#d1fae5', color: '#065f46', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>DEFAULT</span>
              )}
              <div className="vendor-info-grid">
                <div>
                  <p className="info-label">Bank Name</p>
                  <p className="info-value">{bank.bank_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Account Number</p>
                  <p className="info-value">{bank.account_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">IFSC Code</p>
                  <p className="info-value">{bank.ifsc_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="info-label">Branch Address</p>
                  <p className="info-value">{bank.branch_address || 'N/A'}</p>
                </div>
              </div>
            </div>
          )) : (
            <p style={{ color: 'var(--text-light)', fontStyle: 'italic' }}>No bank details provided.</p>
          )}

          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>Documents</h3>
          <div className="vendor-info-grid">
            <div>
              <p className="info-label">NDA Document</p>
              <p className="info-value">
                {vendor.nda_document_url ? (
                  <a href={vendor.nda_document_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Document
                  </a>
                ) : 'Not Uploaded'}
              </p>
            </div>
            <div>
              <p className="info-label">Agreement Document</p>
              <p className="info-value">
                {vendor.agreement_document_url ? (
                  <a href={vendor.agreement_document_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Document
                  </a>
                ) : 'Not Uploaded'}
              </p>
            </div>
          </div>

          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem' }}>Commercials/Quotation</h3>
          <div className="vendor-info-grid" style={{ marginBottom: '1.5rem' }}>
            <div>
              <p className="info-label">Parameter</p>
              <p className="info-value">{vendor.commercial_parameter || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Input Type</p>
              <p className="info-value">{vendor.commercial_input_type || 'N/A'}</p>
            </div>
            <div>
              <p className="info-label">Value</p>
              <p className="info-value">{vendor.commercial_value ? vendor.commercial_value : 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      {(activeTab === 'invoices' || activeTab === 'documents') && (
        <>
          <div className="actions-header">
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="filter-group">
                <label>Doc Type</label>
                <select value={filterDocType} onChange={(e) => setFilterDocType(e.target.value)}>
                  <option value="">All Types</option>
                  {Array.from(new Set(entries
                    .filter(e => activeTab === 'invoices' ? e.doc_type?.toLowerCase().includes('invoice') : !e.doc_type?.toLowerCase().includes('invoice'))
                    .map(e => e.doc_type)
                  )).filter(Boolean).map(type => (
                    <option key={String(type)} value={String(type)}>{String(type)}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Date Issued (From)</label>
                <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
              </div>
              <div className="filter-group">
                <label>Date Issued (To)</label>
                <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
              </div>
              {(filterDocType || filterStartDate || filterEndDate) && (
                <button className="secondary-btn" style={{ height: '40px', padding: '0 1rem', display: 'flex', alignItems: 'center' }} onClick={() => { setFilterDocType(''); setFilterStartDate(''); setFilterEndDate(''); }}>
                  Clear Filters
                </button>
              )}
              <button className="primary-btn" style={{ height: '40px', padding: '0 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={() => setShowUploadModal(true)}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {activeTab === 'invoices' ? 'Upload Invoice' : 'Upload Document'}
              </button>
            </div>
          </div>

          <div className="list-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>No.</th>
                  {activeTab === 'invoices' && <th>Invoice ID</th>}
                  <th>Doc Type</th>
                  <th>Generated By</th>
                  <th>Department</th>
                  <th>Issued Date</th>
                  {activeTab === 'invoices' && (
                    <>
                      <th>Due Date</th>
                      <th>Received Date</th>
                      <th>Invoice Amt</th>
                      {isFinanceDept && <th>Pay Date</th>}
                      <th>Paid Amt</th>
                      <th>Due Amt</th>
                      <th>Pay Status</th>
                      <th>UTR Number</th>
                    </>
                  )}
                  <th>Document</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.filter(ent => {
                  const isInvoice = ent.doc_type?.toLowerCase().includes('invoice');
                  if (activeTab === 'invoices' && !isInvoice) return false;
                  if (activeTab === 'documents' && isInvoice) return false;

                  if (filterDocType && ent.doc_type !== filterDocType) return false;
                  if (filterStartDate && ent.date_issued < filterStartDate) return false;
                  if (filterEndDate && ent.date_issued > filterEndDate) return false;
                  return true;
                }).map((ent, idx) => (
                  <tr
                    key={ent.id}
                    onClick={() => setSelectedEntry(ent)}
                    className="clickable-row"
                    style={activeTab === 'invoices' ? getInvoiceHighlightStyle(ent.received_by_accounts_date, ent.payment_status) : {}}
                  >
                    <td><strong>{idx + 1}</strong></td>
                    {activeTab === 'invoices' && <td>{ent.invoice_id || '-'}</td>}
                    <td>{ent.doc_type}</td>
                    <td>{ent.uploaded_by || '-'}</td>
                    <td>{ent.department || '-'}</td>
                    <td>{ent.date_issued || '-'}</td>
                    {activeTab === 'invoices' && (
                      <>
                        <td>{ent.due_date || '-'}</td>
                        <td>{ent.received_by_accounts_date || '-'}</td>
                        <td>{ent.invoice_amount ? `₹${ent.invoice_amount}` : '-'}</td>
                        {isFinanceDept && <td>{ent.payment_date || '-'}</td>}
                        <td>{ent.amount_paid ? `₹${ent.amount_paid}` : '-'}</td>
                        <td>{ent.doc_type?.toLowerCase().includes('invoice') && ent.invoice_amount ? `₹${Math.max(0, parseFloat(ent.invoice_amount) - (parseFloat(ent.amount_paid) || 0)).toFixed(2)}` : '-'}</td>
                        <td>
                          {ent.payment_status ? (
                            <span className="status-badge" style={{
                              backgroundColor: ent.payment_status === 'paid' ? '#d1fae5' : ent.payment_status === 'partially_paid' ? '#fef3c7' : '#fce7f3',
                              color: ent.payment_status === 'paid' ? '#065f46' : ent.payment_status === 'partially_paid' ? '#92400e' : '#9d174d'
                            }}>
                              {ent.payment_status === 'paid' ? 'Paid' : ent.payment_status === 'partially_paid' ? 'Partial' : 'Unpaid'}
                            </span>
                          ) : '-'}
                        </td>
                        <td>{ent.utr_number || '-'}</td>
                      </>
                    )}
                    <td>
                      {ent.current_version > 0 ? (
                        <div className="action-cell">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16" style={{ color: 'var(--primary)' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span style={{ color: 'var(--primary)', fontWeight: 600 }}>v{ent.current_version}</span>
                        </div>
                      ) : (
                        <span className="text-muted">No file (manual entry)</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${ent.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                        {ent.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-cell">
                        {ent.current_version > 0 && canDownload && (
                          <button
                            className="secondary-btn sm-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadCurrent(ent.id);
                            }}
                            title="Download"
                          >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </button>
                        )}
                        {ent.status === 'active' && (
                          <>
                            <button
                              className="secondary-btn sm-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingEntry(ent);
                              }}
                            >
                              {ent.current_version > 0 ? `Upload v${ent.current_version + 1}` : 'Upload File'}
                            </button>
                            <button
                              className="danger-btn sm-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeactivate(ent.id);
                              }}
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'products' && (
        <div className="list-container">
          <div className="actions-header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button className="primary-btn" onClick={() => navigate(`/vms/vendor/edit/${vendor.id}`, { state: { vendor } })}>
              Edit Products
            </button>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Product Name</th>
                <th>Description</th>
                <th>Quoted Price</th>
                <th>Quantity</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>No products found for this vendor.</td>
                </tr>
              ) : (
                products.map((prod, idx) => (
                  <tr key={prod.id}>
                    <td>{idx + 1}</td>
                    <td><strong>{prod.product_name}</strong></td>
                    <td>{prod.description || '-'}</td>
                    <td>₹{prod.quoted_price}</td>
                    <td>{prod.quantity}</td>
                    <td>
                      <span className={`status-badge ${prod.status === 'active' ? 'status-active' : 'status-inactive'}`} style={{ textTransform: 'capitalize' }}>
                        {prod.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {(showUploadModal || !!editingEntry) && vendor && (
        <AddEntryModal
          isOpen={showUploadModal || !!editingEntry}
          vendorId={id!}
          vendor={vendor}
          onClose={() => { setShowUploadModal(false); setEditingEntry(null); }}
          onSuccess={handleUploadSuccess}
          initialData={editingEntry}
          tabType={activeTab as 'invoices' | 'documents'}
        />
      )}

      <DocVersionsModal
        isOpen={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        vendorId={id!}
        entry={selectedEntry}
        onEdit={() => {
          setEditingEntry(selectedEntry);
          setSelectedEntry(null);
        }}
      />
    </div>
  );
};

export default VendorDetails;
